// ─────────────────────────────────────────────────────────────
// CreateCardDialog — modal form for creating a new feature card.
// Used from the backlog view and from Jamie chat confirmation.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import type { CardCreatePayload, CardStatus, CardSource, PriorityLevel, ValueLevel } from "../types";
import { cardsApi, tagsApi } from "../api/client";

interface CreateCardDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  prefill?: Partial<CardCreatePayload>;
}

const STATUSES: CardStatus[] = ["Idea", "Ready", "Candidate"];
const SOURCES: CardSource[] = [
  "Customer Request",
  "Internal Idea",
  "Support Ticket",
  "Bug Report",
  "Research",
  "Other",
];
const PRIORITIES: PriorityLevel[] = ["Low", "Medium", "High", "Urgent"];
const VALUES: ValueLevel[] = ["Nice to Have", "Low", "Medium", "High", "Critical"];

export function CreateCardDialog({
  open,
  onClose,
  onCreated,
  prefill,
}: CreateCardDialogProps) {
  const [title, setTitle] = useState(prefill?.title ?? "");
  const [description, setDescription] = useState(prefill?.description ?? "");
  const [status, setStatus] = useState<CardStatus>(prefill?.status ?? "Idea");
  const [priority, setPriority] = useState<PriorityLevel>(prefill?.priority ?? "Medium");
  const [value, setValue] = useState<ValueLevel>(prefill?.value ?? "Medium");
  const [source, setSource] = useState<CardSource>(prefill?.source ?? "Internal Idea");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(prefill?.tags ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suggested tags from the API
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);

  useEffect(() => {
    tagsApi.suggested().then((res) => setSuggestedTags(res.tags)).catch(() => {});
  }, []);

  const addTag = (tag?: string) => {
    const t = (tag ?? tagInput).trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    if (!tag) setTagInput("");
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const authUser = (() => {
        try { return JSON.parse(localStorage.getItem("auth_user") || "null"); } catch { return null; }
      })();
      const submittedBy = authUser?.full_name ?? null;
      const payload: CardCreatePayload = {
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority,
        value,
        source,
        tags,
        submitted_by: submittedBy,
      };
      await cardsApi.create(payload);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create card");
    } finally {
      setSaving(false);
    }
  };

  const availableSuggestions = suggestedTags.filter((t) => !tags.includes(t));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>New Feature Card</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder="What is this? Who does it help?"
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as CardStatus)}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as PriorityLevel)}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Value</InputLabel>
              <Select
                label="Value"
                value={value}
                onChange={(e) => setValue(e.target.value as ValueLevel)}
              >
                {VALUES.map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                label="Source"
                value={source}
                onChange={(e) => setSource(e.target.value as CardSource)}
              >
                {SOURCES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tags */}
          <Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.75 }}>
              <TextField
                label="Add tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                size="small"
                sx={{ flex: 1 }}
                placeholder="Custom tag…"
              />
              <Button onClick={() => addTag()} size="small" variant="outlined">Add</Button>
            </Box>

            {/* Active tags */}
            {tags.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => setTags((prev) => prev.filter((t) => t !== tag))}
                    sx={{ borderRadius: "3px", fontSize: "0.75rem" }}
                  />
                ))}
              </Box>
            )}

            {/* Suggested tags */}
            {availableSuggestions.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {availableSuggestions.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    variant="outlined"
                    onClick={() => addTag(t)}
                    sx={{
                      borderRadius: "3px",
                      fontSize: "0.65rem",
                      cursor: "pointer",
                      opacity: 0.65,
                      "&:hover": { opacity: 1 },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={12} /> : undefined}
        >
          {saving ? "Creating…" : "Create card"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
